import React, { useState, useEffect, useCallback, useMemo } from "react";
import Icon from "../AppIcon";
import { tenantsApi } from "../../services/api/admin";
import { integrationsApi } from "../../services/api/integrations";
import { apiClient } from "../../services/api/client";

// Build steps dynamically based on tenant config
const buildSteps = (tenant) => {
  const steps = [
    { id: "welcome", title: "Welcome", icon: "Sparkles" },
    { id: "company", title: "Company Details", icon: "Building2" },
  ];

  // Only show TroncMaster invitation step when TroncMaster Mode is on AND employer distribution is NOT enabled
  // (employerDistributionEnabled tenants manage their own distributions — no TroncMaster required)
  if (tenant?.troncMasterMode && !tenant?.employerDistributionEnabled) {
    steps.push({ id: "troncmaster", title: "TRONC Master", icon: "Shield" });
  }

  steps.push(
    { id: "integrations", title: "Integrations", icon: "Link" },
    { id: "sync", title: "Sync Data", icon: "RefreshCw" },
    { id: "done", title: "All Done", icon: "PartyPopper" },
  );

  return steps;
};

const OnboardingWizard = ({ tenant, user, onComplete, onSkip }) => {
  const STEPS = useMemo(() => buildSteps(tenant), [tenant?.id, tenant?.troncMasterMode, tenant?.employerDistributionEnabled]);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  // Company form
  const [companyForm, setCompanyForm] = useState({
    name: tenant?.name || "",
    contactEmail: tenant?.contactEmail || "",
    contactPhone: tenant?.contactPhone || "",
    addressLine1: tenant?.addressLine1 || "",
    addressLine2: tenant?.addressLine2 || "",
    city: tenant?.city || "",
    county: tenant?.county || "",
    postCode: tenant?.postCode || "",
  });

  // TroncMaster invitation form
  const [troncMasterForm, setTroncMasterForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });
  const [troncMasterStatus, setTroncMasterStatus] = useState(null); // null | 'checking' | 'exists' | 'invited' | 'none'
  const [troncMasterErrors, setTroncMasterErrors] = useState({});

  // Integration state
  const [integrations, setIntegrations] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [selectedHr, setSelectedHr] = useState(null);
  const [selectedTill, setSelectedTill] = useState(null);
  const [hrConfiguring, setHrConfiguring] = useState(false);
  const [tillConfiguring, setTillConfiguring] = useState(false);
  const [configForm, setConfigForm] = useState({ apiKey: "", baseUrl: "" });

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Check if a TroncMaster already exists when reaching that step
  useEffect(() => {
    const step = STEPS[currentStep];
    if (step?.id !== "troncmaster") return;

    const checkTroncMaster = async () => {
      setTroncMasterStatus("checking");
      try {
        const usersRes = await apiClient.get("/users");
   const users = usersRes?.data ?? usersRes?.Data ?? [];
        const activeTM = users.find((u) => u.role === "TroncMaster" && u.isActive);
  if (activeTM) {
 setTroncMasterStatus("exists");
          return;
        }

        const invRes = await apiClient.get("/users/invitations");
        const invitations = invRes?.data ?? invRes?.Data ?? [];
        const pendingTM = invitations.find((i) => i.role === "TroncMaster" && i.status === "Pending");
        if (pendingTM) {
        setTroncMasterStatus("invited");
          return;
        }

        setTroncMasterStatus("none");
      } catch (err) {
        console.error("Failed to check TroncMaster:", err);
     setTroncMasterStatus("none");
      }
    };

    checkTroncMaster();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const showNotification = (message, type = "success") => {
  setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSaveCompany = async () => {
 if (!tenant?.id) return;
    setSaving(true);
    try {
  await tenantsApi.update(tenant.id, {
  name: companyForm.name,
 contactEmail: companyForm.contactEmail,
        contactPhone: companyForm.contactPhone || null,
      addressLine1: companyForm.addressLine1 || null,
        addressLine2: companyForm.addressLine2 || null,
   city: companyForm.city || null,
        county: companyForm.county || null,
        postCode: companyForm.postCode || null,
      });
   showNotification("Company details saved");
    setCurrentStep(currentStep + 1);
    } catch (err) {
      showNotification(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleConnectProvider = async (provider, type) => {
    setSaving(true);
    try {
      const isRocket =
  provider.providerCode === "RocketHR" ||
 provider.providerCode === "RocketPOS";

      await integrationsApi.saveSettings({
   integrationProviderId: provider.providerId,
  isEnabled: true,
        apiKey: isRocket ? null : configForm.apiKey || null,
        baseUrl: isRocket ? null : configForm.baseUrl || null,
  });

   showNotification(`${provider.providerName} connected`);
    if (type === "hr") {
        setSelectedHr(provider);
        setHrConfiguring(false);
      } else {
        setSelectedTill(provider);
        setTillConfiguring(false);
      }
      setConfigForm({ apiKey: "", baseUrl: "" });
      fetchIntegrations();
    } catch (err) {
 showNotification(err.message || "Failed to connect", "error");
    } finally {
    setSaving(false);
    }
  };

  const handleFullSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const hrResult = await integrationsApi.fullHrSync();
      let tillResult = null;
      if (selectedTill) {
      tillResult = await integrationsApi.syncTroncRecords({});
      }
  setSyncResult({
        hr: hrResult.success ? hrResult.data : null,
        till: tillResult?.success ? tillResult.data : null,
      });
      showNotification("Sync completed successfully");
    } catch (err) {
      showNotification(err.message || "Sync failed", "error");
    } finally {
      setSyncing(false);
    }
  };

  const handleFinish = async () => {
    if (!tenant?.id) return;
    setSaving(true);
    try {
  await tenantsApi.completeOnboarding(tenant.id);
      onComplete();
    } catch (err) {
 console.error("Failed to mark onboarding complete:", err);
      onComplete(); // Still proceed
    } finally {
      setSaving(false);
    }
  };

  const handleInviteTroncMaster = async () => {
    const errors = {};
    if (!troncMasterForm.email) errors.email = "Email is required";
    if (!troncMasterForm.firstName) errors.firstName = "First name is required";
    if (!troncMasterForm.lastName) errors.lastName = "Last name is required";
    if (Object.keys(errors).length > 0) {
      setTroncMasterErrors(errors);
      return;
    }
    setTroncMasterErrors({});
    setSaving(true);
    try {
      const response = await apiClient.post("/users/invite", {
        email: troncMasterForm.email,
        firstName: troncMasterForm.firstName,
        lastName: troncMasterForm.lastName,
      role: "TroncMaster",
     personalMessage:
        "You have been invited as the TRONC Master. As TRONC Master, you will be responsible for building distribution rules and approving TRONC distributions. " +
       "By accepting, you confirm that you do NOT have hiring or firing authority, are NOT a business decision-maker regarding employment terms, and will act independently of employer influence (HMRC requirement).",
      });
      if (response.success) {
 showNotification("TRONC Master invitation sent!");
  setTroncMasterStatus("invited");
    } else {
        showNotification(response.message || "Failed to send invitation", "error");
      }
    } catch (err) {
    showNotification(err.message || "Failed to send invitation", "error");
    } finally {
      setSaving(false);
}
  };

  const fetchIntegrations = useCallback(async () => {
    setIntegrationsLoading(true);
    try {
      const response = await integrationsApi.getSettingsOverview();
      if (response.success) {
        setIntegrations(response.data);
// Pre-select any already-configured providers
   const activeHr = response.data?.hrProviders?.find(
        (p) => p.isEnabled && p.isConfigured,
        );
        const activeTill = response.data?.tillProviders?.find(
     (p) => p.isEnabled && p.isConfigured,
        );
        if (activeHr) setSelectedHr(activeHr);
        if (activeTill) setSelectedTill(activeTill);
      }
    } catch (err) {
      console.error("Failed to load integrations:", err);
    } finally {
 setIntegrationsLoading(false);
    }
  }, []);

  useEffect(() => {
    const step = STEPS[currentStep];
    if (step?.id === "integrations") {
  fetchIntegrations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const step = STEPS[currentStep];

  return (
<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="w-full max-w-2xl mx-4">
        {/* Notification */}
        {notification && (
       <div
            className={`mb-4 p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
            notification.type === "error"
  ? "bg-red-50 text-red-700 border border-red-200"
             : "bg-green-50 text-green-700 border border-green-200"
            }`}
    >
            <Icon
              name={notification.type === "error" ? "AlertCircle" : "CheckCircle"}
      size={16}
         />
     {notification.message}
          </div>
      )}

    {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.map((s, i) => (
         <div key={s.id} className="flex items-center gap-2">
      <div
    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
      i < currentStep
      ? "bg-success text-white"
   : i === currentStep
             ? "bg-primary text-white"
      : "bg-muted text-muted-foreground"
      }`}
           >
                {i < currentStep ? (
           <Icon name="Check" size={14} />
   ) : (
         i + 1
    )}
      </div>
       {i < STEPS.length - 1 && (
    <div
        className={`w-8 h-0.5 ${
       i < currentStep ? "bg-success" : "bg-muted"
          }`}
     />
      )}
       </div>
          ))}
        </div>

        {/* Card */}
    <div className="bg-card rounded-2xl shadow-xl border border-border overflow-hidden">
{/* Step: Welcome */}
   {step.id === "welcome" && (
     <div className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
  <Icon name="Rocket" size={40} className="text-primary" />
      </div>
          <h1 className="text-3xl font-heading font-bold mb-3">
  Welcome to Rocket Tronc!
      </h1>
  <p className="text-lg text-muted-foreground mb-2">
    Hi {user?.firstName || "there"}, let's get{" "}
   <strong>{tenant?.name || "your company"}</strong> set up.
   </p>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          This quick wizard will help you configure your company details,
      connect your HR and Till systems, and sync your data. It only
                takes a couple of minutes.
        </p>
        <div className="flex items-center justify-center gap-3">
        <button
      onClick={() => setCurrentStep(1)}
 className="btn btn-primary btn-lg gap-2"
       >
            Let's Get Started
                <Icon name="ArrowRight" size={18} />
      </button>
  </div>
      <button
        onClick={onSkip}
            className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
       >
      Skip for now — I'll set up later
           </button>
   </div>
 )}

          {/* Step: Company Details */}
          {step.id === "company" && (
  <div className="p-8">
  <div className="text-center mb-6">
       <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
   <Icon name="Building2" size={24} className="text-primary" />
             </div>
 <h2 className="text-2xl font-heading font-bold">
 Company Details
          </h2>
     <p className="text-muted-foreground mt-1">
      Confirm or update your company information
       </p>
         </div>

        <div className="space-y-4 max-w-lg mx-auto">
             <div>
       <label className="block text-sm font-medium mb-1">
  Company Name <span className="text-error">*</span>
   </label>
          <input
       type="text"
     value={companyForm.name}
   onChange={(e) =>
        setCompanyForm({ ...companyForm, name: e.target.value })
           }
          className="input w-full"
         placeholder="Your Company Ltd"
      />
                </div>
      <div className="grid grid-cols-2 gap-4">
  <div>
            <label className="block text-sm font-medium mb-1">
         Contact Email
        </label>
     <input
      type="email"
    value={companyForm.contactEmail}
               onChange={(e) =>
              setCompanyForm({
          ...companyForm,
            contactEmail: e.target.value,
          })
         }
          className="input w-full"
  />
    </div>
           <div>
         <label className="block text-sm font-medium mb-1">
     Phone
     </label>
            <input
         type="tel"
 value={companyForm.contactPhone}
  onChange={(e) =>
           setCompanyForm({
            ...companyForm,
  contactPhone: e.target.value,
      })
             }
     className="input w-full"
      />
           </div>
                </div>
      <div>
      <label className="block text-sm font-medium mb-1">
                 Address
      </label>
   <input
        type="text"
            value={companyForm.addressLine1}
         onChange={(e) =>
 setCompanyForm({
        ...companyForm,
      addressLine1: e.target.value,
                    })
        }
   className="input w-full mb-2"
           placeholder="Address line 1"
              />
        <input
           type="text"
     value={companyForm.addressLine2}
          onChange={(e) =>
             setCompanyForm({
    ...companyForm,
     addressLine2: e.target.value,
          })
 }
        className="input w-full"
                  placeholder="Address line 2"
  />
            </div>
                <div className="grid grid-cols-3 gap-4">
     <div>
       <label className="block text-sm font-medium mb-1">
              City
   </label>
       <input
  type="text"
      value={companyForm.city}
 onChange={(e) =>
  setCompanyForm({ ...companyForm, city: e.target.value })
    }
         className="input w-full"
           />
    </div>
      <div>
     <label className="block text-sm font-medium mb-1">
               County
     </label>
       <input
     type="text"
  value={companyForm.county}
     onChange={(e) =>
    setCompanyForm({
 ...companyForm,
           county: e.target.value,
   })
             }
   className="input w-full"
         />
     </div>
  <div>
    <label className="block text-sm font-medium mb-1">
    Post Code
                </label>
        <input
          type="text"
             value={companyForm.postCode}
 onChange={(e) =>
                    setCompanyForm({
         ...companyForm,
     postCode: e.target.value,
     })
         }
      className="input w-full"
    />
   </div>
        </div>
          </div>

   <div className="flex items-center justify-between mt-8">
 <button
               onClick={() => setCurrentStep(0)}
      className="btn btn-ghost gap-1"
  >
       <Icon name="ArrowLeft" size={16} />
    Back
     </button>
    <button
       onClick={handleSaveCompany}
     disabled={saving || !companyForm.name}
        className="btn btn-primary gap-1"
          >
        {saving ? "Saving..." : "Save & Continue"}
  <Icon name="ArrowRight" size={16} />
    </button>
        </div>
       </div>
 )}
{/* Step: TRONC Master Invitation (conditional) */
          }
          {step.id === "troncmaster" && (
          <div className="p-8">
              <div className="text-center mb-6">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Icon name="Shield" size={24} className="text-primary" />
       </div>
  <h2 className="text-2xl font-heading font-bold">
        Invite a TRONC Master
       </h2>
       <p className="text-muted-foreground mt-1">
      TRONC Master Mode is enabled. A TRONC Master is required for HMRC-compliant distribution.
     </p>
          </div>

      {troncMasterStatus === "checking" && (
  <div className="text-center py-8">
            <div className="spinner mx-auto mb-3" />
     <p className="text-muted-foreground">Checking TRONC Master status...</p>
       </div>
        )}

              {troncMasterStatus === "exists" && (
     <div className="max-w-md mx-auto">
                  <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
          <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
    <div>
          <p className="font-semibold text-success">TRONC Master Active</p>
             <p className="text-sm text-muted-foreground mt-1">
       A TRONC Master is already assigned to this tenant. You're all set.
            </p>
        </div>
            </div>
       </div>
              )}

       {troncMasterStatus === "invited" && (
    <div className="max-w-md mx-auto">
      <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-start gap-3">
       <Icon name="Mail" size={20} className="text-primary mt-0.5" />
            <div>
            <p className="font-semibold text-primary">Invitation Sent</p>
      <p className="text-sm text-muted-foreground mt-1">
       A TRONC Master invitation is pending. They will need to accept before they can manage distributions.
       </p>
         </div>
 </div>
     </div>
           )}

   {troncMasterStatus === "none" && (
      <div className="max-w-lg mx-auto space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-amber-600 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold text-amber-800">No TRONC Master Assigned</p>
   <p className="text-sm text-amber-700 mt-1">
           HMRC requires the TRONC Master to be independent of the employer. 
They must <strong>not</strong> have hiring/firing authority and must act independently when distributing TRONC.
          </p>
       </div>
       </div>

        <div className="space-y-3">
     <div>
      <label className="block text-sm font-medium mb-1">
          Email <span className="text-error">*</span>
   </label>
  <input
          type="email"
       value={troncMasterForm.email}
    onChange={(e) => setTroncMasterForm({ ...troncMasterForm, email: e.target.value })}
       className={`input w-full ${troncMasterErrors.email ? "border-error" : ""}`}
     placeholder="troncmaster@example.com"
           />
     {troncMasterErrors.email && <p className="text-xs text-error mt-1">{troncMasterErrors.email}</p>}
       </div>
         <div className="grid grid-cols-2 gap-3">
          <div>
         <label className="block text-sm font-medium mb-1">
        First Name <span className="text-error">*</span>
               </label>
     <input
       type="text"
  value={troncMasterForm.firstName}
       onChange={(e) => setTroncMasterForm({ ...troncMasterForm, firstName: e.target.value })}
     className={`input w-full ${troncMasterErrors.firstName ? "border-error" : ""}`}
          />
            {troncMasterErrors.firstName && <p className="text-xs text-error mt-1">{troncMasterErrors.firstName}</p>}
          </div>
               <div>
     <label className="block text-sm font-medium mb-1">
             Last Name <span className="text-error">*</span>
          </label>
<input
              type="text"
     value={troncMasterForm.lastName}
           onChange={(e) => setTroncMasterForm({ ...troncMasterForm, lastName: e.target.value })}
         className={`input w-full ${troncMasterErrors.lastName ? "border-error" : ""}`}
         />
    {troncMasterErrors.lastName && <p className="text-xs text-error mt-1">{troncMasterErrors.lastName}</p>}
       </div>
          </div>
           <button
      onClick={handleInviteTroncMaster}
         disabled={saving}
  className="btn btn-primary w-full gap-2"
     >
          {saving ? (
       <>
       <div className="spinner spinner-sm" />
     Sending Invitation...
            </>
    ) : (
              <>
                <Icon name="Send" size={16} />
    Send TRONC Master Invitation
             </>
          )}
       </button>
             </div>
         </div>
    )}

        <div className="flex items-center justify-between mt-8">
      <button
       onClick={() => setCurrentStep(currentStep - 1)}
                  className="btn btn-ghost gap-1"
     >
      <Icon name="ArrowLeft" size={16} />
       Back
       </button>
<button
            onClick={() => setCurrentStep(currentStep + 1)}
     className="btn btn-primary gap-1"
              >
            {troncMasterStatus === "none" ? "Skip for Now" : "Continue"}
             <Icon name="ArrowRight" size={16} />
     </button>
              </div>
      </div>
          )}

   {/* Step: Integrations */}
    {step.id === "integrations" && (
  <div className="p-8">
   <div className="text-center mb-6">
    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Icon name="Link" size={24} className="text-primary" />
        </div>
        <h2 className="text-2xl font-heading font-bold">
        Connect Your Systems
          </h2>
      <p className="text-muted-foreground mt-1">
     Choose your HR and Till/POS providers
         </p>
     </div>

     {integrationsLoading ? (
                <div className="text-center py-8">
      <div className="spinner mx-auto mb-3" />
        <p className="text-muted-foreground">
      Loading available integrations...
    </p>
           </div>
              ) : (
   <div className="space-y-6 max-w-lg mx-auto">
          {/* HR Provider */}
 <div>
   <h3 className="font-semibold mb-3 flex items-center gap-2">
       <Icon name="Users" size={18} className="text-primary" />
  HR System
         </h3>
        {selectedHr ? (
     <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center gap-2">
    <Icon
         name="CheckCircle"
          size={18}
         className="text-success"
      />
         <span className="font-medium">
      {selectedHr.providerName}
          </span>
             </div>
             <span className="badge badge-success text-xs">
 Connected
       </span>
          </div>
      ) : hrConfiguring ? (
          <div className="border border-border rounded-lg p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
     Enter API credentials for{" "}
           {hrConfiguring.providerName}
         </p>
            {hrConfiguring.providerCode === "RocketHR" ? (
        <p className="text-sm text-success font-medium">
No credentials needed — click Connect.
       </p>
      ) : (
     <>
    <input
     type="text"
     value={configForm.apiKey}
        onChange={(e) =>
       setConfigForm({
         ...configForm,
apiKey: e.target.value,
        })
       }
             className="input w-full"
           placeholder="API Key"
      />
 <input
        type="url"
        value={configForm.baseUrl}
  onChange={(e) =>
         setConfigForm({
   ...configForm,
               baseUrl: e.target.value,
         })
            }
      className="input w-full"
   placeholder="Base URL (optional)"
    />
       </>
         )}
   <div className="flex gap-2">
 <button
           onClick={() => setHrConfiguring(false)}
        className="btn btn-ghost btn-sm"
         >
     Cancel
        </button>
      <button
    onClick={() =>
  handleConnectProvider(hrConfiguring, "hr")
     }
      disabled={saving}
         className="btn btn-primary btn-sm"
     >
    {saving ? "Connecting..." : "Connect"}
     </button>
       </div>
              </div>
) : (
   <div className="grid grid-cols-2 gap-2">
    {integrations?.hrProviders
         ?.filter((p) => p.isProviderActive)
      .map((provider) => (
<button
       key={provider.providerId}
     onClick={() => setHrConfiguring(provider)}
     className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
         >
         <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
       <Icon name="Users" size={16} />
   </div>
     <div>
  <p className="font-medium text-sm">
      {provider.providerName}
    </p>
          <p className="text-xs text-muted-foreground">
      {provider.providerCode}
       </p>
             </div>
        </button>
           ))}
            </div>
        )}
         </div>

  {/* Till Provider */}
          <div>
      <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Icon
         name="PoundSterling"
     size={18}
    className="text-secondary"
           />
          Till / POS System
    </h3>
    {selectedTill ? (
    <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-lg">
   <div className="flex items-center gap-2">
         <Icon
      name="CheckCircle"
     size={18}
     className="text-success"
       />
   <span className="font-medium">
                  {selectedTill.providerName}
         </span>
            </div>
     <span className="badge badge-success text-xs">
  Connected
             </span>
 </div>
        ) : tillConfiguring ? (
          <div className="border border-border rounded-lg p-4 space-y-3">
             <p className="text-sm text-muted-foreground">
  Enter API credentials for{" "}
     {tillConfiguring.providerName}
 </p>
            {tillConfiguring.providerCode === "RocketPOS" ? (
     <p className="text-sm text-success font-medium">
              No credentials needed — click Connect.
           </p>
          ) : (
           <>
   <input
 type="text"
   value={configForm.apiKey}
          onChange={(e) =>
        setConfigForm({
     ...configForm,
         apiKey: e.target.value,
  })
        }
       className="input w-full"
        placeholder="API Key"
         />
    <input
         type="url"
       value={configForm.baseUrl}
          onChange={(e) =>
             setConfigForm({
      ...configForm,
        baseUrl: e.target.value,
         })
    }
             className="input w-full"
 placeholder="Base URL (optional)"
              />
 </>
       )}
    <div className="flex gap-2">
                 <button
         onClick={() => setTillConfiguring(false)}
      className="btn btn-ghost btn-sm"
             >
       Cancel
    </button>
               <button
    onClick={() =>
             handleConnectProvider(tillConfiguring, "till")
    }
        disabled={saving}
     className="btn btn-primary btn-sm"
     >
         {saving ? "Connecting..." : "Connect"}
              </button>
            </div>
     </div>
      ) : (
<div className="grid grid-cols-2 gap-2">
          {integrations?.tillProviders
        ?.filter((p) => p.isProviderActive)
  .map((provider) => (
         <button
        key={provider.providerId}
                   onClick={() => setTillConfiguring(provider)}
       className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
  >
    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
             <Icon name="CreditCard" size={16} />
      </div>
    <div>
 <p className="font-medium text-sm">
              {provider.providerName}
</p>
             <p className="text-xs text-muted-foreground">
             {provider.providerCode}
           </p>
        </div>
       </button>
          ))}
          </div>
       )}
          </div>
       </div>
    )}

 {/* Integrations step: Back button */}
        <div className="flex items-center justify-between mt-8">
     <button
    onClick={() => setCurrentStep(currentStep - 1)}
className="btn btn-ghost gap-1"
         >
        <Icon name="ArrowLeft" size={16} />
          Back
  </button>
      <button
   onClick={() => setCurrentStep(currentStep + 1)}
      className="btn btn-primary gap-1"
    >
      {selectedHr || selectedTill ? "Continue to Sync" : "Skip"}
      <Icon name="ArrowRight" size={16} />
    </button>
      </div>
            </div>
          )}

  {/* Step: Sync */}
  {step.id === "sync" && (
            <div className="p-8">
              <div className="text-center mb-6">
           <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
   <Icon name="RefreshCw" size={24} className="text-primary" />
         </div>
  <h2 className="text-2xl font-heading font-bold">
 Sync Your Data
      </h2>
   <p className="text-muted-foreground mt-1">
          {selectedHr || selectedTill
      ? "Pull in your locations, employees, and records"
         : "No integrations connected — you can sync later from Settings."}
      </p>
       </div>

   <div className="max-w-md mx-auto">
      {selectedHr || selectedTill ? (
     <>
  {!syncResult ? (
      <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-4">
       This will sync:
          </p>
           <ul className="text-sm text-left inline-block space-y-2 mb-6">
        {selectedHr && (
         <>
   <li className="flex items-center gap-2">
       <Icon name="Building2" size={14} className="text-primary" />
      Locations
              </li>
   <li className="flex items-center gap-2">
       <Icon name="Briefcase" size={14} className="text-primary" />
          Job roles
      </li>
           <li className="flex items-center gap-2">
              <Icon name="Users" size={14} className="text-primary" />
 Employees
          </li>
         </>
             )}
               {selectedTill && (
   <li className="flex items-center gap-2">
       <Icon name="Banknote" size={14} className="text-secondary" />
              TRONC records (last 7 days)
     </li>
    )}
              </ul>
       <button
          onClick={handleFullSync}
      disabled={syncing}
         className="btn btn-primary btn-lg gap-2 w-full"
 >
           {syncing ? (
         <>
 <div className="spinner spinner-sm" />
      Syncing data...
       </>
   ) : (
    <>
 <Icon name="RefreshCw" size={18} />
                Run Full Sync
              </>
        )}
  </button>
            </div>
    ) : (
   <div className="space-y-3">
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg text-center">
         <Icon
                 name="CheckCircle2"
        size={32}
           className="text-success mx-auto mb-2"
         />
        <p className="font-semibold">Sync Complete!</p>
           </div>
   {syncResult.hr && (
       <div className="text-sm space-y-1 p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">HR Data:</p>
           <p className="text-muted-foreground">
   Created: {syncResult.hr.totalCreated || 0} •
            Updated: {syncResult.hr.totalUpdated || 0}
             </p>
           </div>
               )}
       {syncResult.till && (
   <div className="text-sm space-y-1 p-3 bg-muted/50 rounded-lg">
 <p className="font-medium">Till Data:</p>
         <p className="text-muted-foreground">
         Created: {syncResult.till.createdCount || 0} •
    Updated: {syncResult.till.updatedCount || 0}
         </p>
   </div>
               )}
     </div>
         )}
  </>
      ) : (
   <div className="text-center py-8">
     <Icon
         name="Info"
   size={24}
     className="text-muted-foreground mx-auto mb-2"
          />
  <p className="text-sm text-muted-foreground">
              You can connect integrations and sync data at any time
      from <strong>Settings → Company</strong> or{" "}
    <strong>System → Integrations</strong>.
   </p>
     </div>
    )}
         </div>

              <div className="flex items-center justify-between mt-8">
      <button
    onClick={() => setCurrentStep(currentStep - 1)}
        className="btn btn-ghost gap-1"
    >
    <Icon name="ArrowLeft" size={16} />
   Back
  </button>
  <button
        onClick={() => setCurrentStep(currentStep + 1)}
    disabled={syncing}
  className="btn btn-primary gap-1"
     >
      {syncResult || !selectedHr ? "Finish Setup" : "Skip Sync"}
   <Icon name="ArrowRight" size={16} />
     </button>
            </div>
     </div>
  )}

   {/* Step: Done */}
     {step.id === "done" && (
            <div className="p-8 text-center">
    <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
       <Icon name="PartyPopper" size={40} className="text-success" />
    </div>
            <h2 className="text-3xl font-heading font-bold mb-3">
          You're All Set!
         </h2>
  <p className="text-lg text-muted-foreground mb-2">
    <strong>{companyForm.name || tenant?.name}</strong> is ready to
       go.
  </p>
         <p className="text-muted-foreground mb-8 max-w-md mx-auto">
      Your dashboard will show your TRONC data and distribution
    activity. You can always adjust settings from the sidebar menu.
          </p>

        <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto mb-8 text-left">
            <div className="p-3 rounded-lg bg-muted/50">
        <Icon name="LayoutDashboard" size={16} className="text-primary mb-1" />
   <p className="text-xs font-medium">Dashboard</p>
        <p className="text-xs text-muted-foreground">Overview & KPIs</p>
    </div>
          <div className="p-3 rounded-lg bg-muted/50">
          <Icon name="PoundSterling" size={16} className="text-primary mb-1" />
     <p className="text-xs font-medium">Daily Takings</p>
        <p className="text-xs text-muted-foreground">Record TRONC data</p>
                </div>
          <div className="p-3 rounded-lg bg-muted/50">
        <Icon name="PieChart" size={16} className="text-primary mb-1" />
    <p className="text-xs font-medium">Distribution</p>
         <p className="text-xs text-muted-foreground">Allocate tips fairly</p>
       </div>
            <div className="p-3 rounded-lg bg-muted/50">
          <Icon name="FileText" size={16} className="text-primary mb-1" />
       <p className="text-xs font-medium">Audit Trail</p>
            <p className="text-xs text-muted-foreground">Full transparency</p>
          </div>
       </div>

   <button
    onClick={handleFinish}
                disabled={saving}
           className="btn btn-primary btn-lg gap-2"
     >
          {saving ? "Finishing..." : "Go to Dashboard"}
                <Icon name="ArrowRight" size={18} />
           </button>
         </div>
          )}
        </div>
</div>
    </div>
  );
};

export default OnboardingWizard;
